import React from 'react';

import { trackEvent } from '@xingternal/hops-tracking';

import { JobAlertContext, Summary } from '@/contexts';
import { useSearchAlerts } from '@/domain/search-alerts/hooks';
import { useUserContactDetailsQuery } from '@/graphql/generated';
import { unscrambleStringId } from '@/helpers/xing-one';
import { useLoginStates } from '@/hooks';

import { CreateAlertLightbox } from './create-alert-lightbox-component';

type Props = {
  summary: Summary;
  searchQueryId?: string;
};

export const CreateAlertLightboxContainer: React.VFC<Props> = ({ summary, searchQueryId }) => {
  const { data, loading } = useUserContactDetailsQuery();
  const { isLoggedOut } = useLoginStates();
  const { createSearchAlert } = useSearchAlerts();

  const [didCreateAlert, setDidCreateAlert] = React.useState(false);
  const [creatingAlert, setCreatingAlert] = React.useState(false);
  const [email, setEmail] = React.useState('');

  React.useEffect(() => {
    if (!loading) setEmail(data?.viewer?.accountSettings?.email?.active ?? '');
  }, [data, loading]);

  const { toggleAlertLightbox, isAlertLightboxOpen, propActionOrigin } = React.useContext(JobAlertContext);

  const onCancel = (action: string) => () => {
    toggleAlertLightbox();

    if (didCreateAlert) {
      trackEvent('PropTrackAction', {
        PropTrackAction: `jobs_search_alert_create_${action}`,
        PropActionOrigin: propActionOrigin,
      });
    }
  };

  const onCreate = async (): Promise<void> => {
    trackEvent('PropTrackAction', {
      PropTrackAction: 'jobs_search_alert_create_confirm',
      PropActionOrigin: propActionOrigin,
    });
    setCreatingAlert(true);

    try {
      const { discipline, ...restSummary } = summary;
      await createSearchAlert({
        alertQuery: {
          ...restSummary,
          ...(discipline && { filter: { discipline: [unscrambleStringId(discipline.id)] } }),
        },
        ...(isLoggedOut && { email }),
        queryId: searchQueryId,
      });
      setDidCreateAlert(true);
    } finally {
      setCreatingAlert(false);
    }
  };

  return (
    <CreateAlertLightbox
      onCancel={onCancel}
      onCreate={onCreate}
      creatingAlert={creatingAlert}
      isAlertLightboxOpen={isAlertLightboxOpen}
      didCreateAlert={didCreateAlert}
      email={email}
      onChangeEmail={setEmail}
      isLoggedOut={isLoggedOut}
      summary={summary}
    />
  );
};
